import { ReactNode, useContext, useMemo, useEffect } from "react";
import ReactGA from "react-ga4";
import { Theme, useTheme } from "@mui/material/styles";
import { Box, useMediaQuery, Alert, Typography } from "@mui/material";
import NavBar from "components/layout/NavBar";
import { Outlet, Link, useLocation, useNavigate } from "react-router";
import { getAnonymizedPath, getCookieValue, setCookieValue } from "utils";
import MobileNavBar from "components/layout/MobileNavBar";
import { ROUTES, routeWithParams } from "router";
import { AppContext } from "context/appContext";
import { useTranslation } from "react-i18next";
import { AUTURA_SHADOWS, MOBILE_LAYOUT } from "context/themeContext";

const MainLayout = (): ReactNode => {
  const theme = useTheme<Theme>();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const { state } = useContext(AppContext);
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    if (!state.routes.length) {
      // check for app state, back to login if no state
      const companyPath = getCookieValue("companyPath");
      navigate(routeWithParams(ROUTES.LOGIN, [companyPath]));
    } else {
      // normal behavior, track page view and set last url
      logPageView();
    }
  }, [state.routes]);

  const logPageView = () => {
    ReactGA.send({
      hitType: "pageview",
      page: getAnonymizedPath(location.pathname),
      search: location.search,
    });
    setCookieValue("lastUrl", `${location.pathname}`);
  };

  const userIsRegistered = useMemo(() => !!state.user?.email, [state.user]);

  return isMobile ? (
    <Box
      sx={{
        display: { xs: "flex", lg: "none" },
        flexDirection: "column",
        height: "100dvh",
        overflow: "hidden",
        backgroundColor: "background.default",
      }}
    >
      {/* Header */}
      <Box
        component="header"
        sx={{
          flexShrink: 0,
          height: MOBILE_LAYOUT.navbarHeight,
          position: "relative",
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        <MobileNavBar />
      </Box>
      {/* Main Content */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Outlet />
      </Box>
    </Box>
  ) : (
    // DESKTOP LAYOUT
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        minHeight: 0,
      }}
    >
      <Box
        component="header"
        sx={{
          flexShrink: 0,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: "primary.main",
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        <Box
          sx={{
            height: "100px",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <NavBar />
        </Box>
        {/* Registration Banner */}
        {!userIsRegistered && (
          <Alert
            severity="warning"
            variant="filled"
            sx={{
              borderRadius: 0,
              "& .MuiAlert-message": {
                width: "100%",
              },
            }}
          >
            <Typography variant={"body1"}>
              {t("registrationBanner")}
              <Link
                to={
                  window.CLIENT_BASE_URL +
                  routeWithParams(ROUTES.REGISTRATION, [
                    state.tenant?.data?.companyPath || "",
                    state.user.jwtToken || "",
                  ])
                }
                color={"info"}
              >
                {t("registrationLink")}
              </Link>
            </Typography>
          </Alert>
        )}
      </Box>
      <Box
        sx={{
          flex: 1,
          overflow: "hidden",
          m: { xs: 0, sm: 0, md: 3 },
        }}
      >
        <Box
          sx={{
            height: "100%",
            minHeight: 0,
            overflow: "auto",
            borderRadius: 1,
            boxShadow: AUTURA_SHADOWS[3],
          }}
        >
          {<Outlet />}
        </Box>
      </Box>
    </Box>
  );
};

export default MainLayout;
